import { isFrozen, type AttackerData } from '../entities/Attacker';
import type { BoardSystem } from './BoardSystem';
import type { QueueSystem } from './QueueSystem';
import type { StackSystem } from './StackSystem';

/**
 * Result of {@link findStuckOnFrozenFrontLane}: the lowest-numbered lane whose
 * front holds a frozen attacker that can be force-dispatched, or null if the
 * state is not stuck.
 */
export interface StuckOnFrozenFrontInfo {
  laneIndex: number;
  attackerId: number;
}

/**
 * Checks if the game is in a deadlock state — no possible action can lead to
 * any cube being cleared or any frozen attacker being thawed.
 *
 * CONSERVATIVE: Only returns true when absolutely certain no progress is possible.
 * It is far worse to false-positive (end a winnable game) than to miss a deadlock.
 */
export function isDeadlocked(
  conveyorAttackerCount: number,
  transitioningCount: number,
  queue: QueueSystem,
  board: BoardSystem,
  stack?: StackSystem,
): boolean {
  // If there are attackers on the conveyor, they will shoot or complete a lap → not deadlocked
  if (conveyorAttackerCount > 0) return false;

  // If there are transitioning attackers (being animated to conveyor), not deadlocked
  if (transitioningCount > 0) return false;

  // If there's a dispatchable attacker or hammer at front of queue → not deadlocked
  if (queue.canDispatchAny()) return false;

  // If the queue has any non-frozen attackers anywhere (not just at lane fronts),
  // don't declare deadlock. They will eventually become dispatchable as frozen items
  // ahead of them thaw (frozen counters decrement when attackers exhaust on the conveyor).
  if (queue.hasAnyNonFrozenItems()) return false;

  // Check if any waiting stack attacker has targets on the board or can thaw frozen attackers
  const stackSlots = stack ? stack.getSlots() : [];
  const hasFrozenInQueue = queueHasFrozenAttackers(queue);

  for (const attacker of stackSlots) {
    if (!attacker) continue;
    // If this attacker has any orb whose color exists on the board → can dispatch it → not deadlocked
    for (const orb of attacker.orbs) {
      if (orb.ammo > 0 && board.hasTargetsForColor(orb.colorIndex)) return false;
    }
    // Even if this attacker has no matching targets, dispatching it will cause it to
    // complete a lap and exhaust, which thaws frozen attackers. If there ARE frozen
    // attackers in the queue, this is still progress.
    if (hasFrozenInQueue) return false;
  }

  // No progress is possible → deadlocked
  return true;
}

/** Returns true if any attacker in the queue lanes is frozen. */
function queueHasFrozenAttackers(queue: QueueSystem): boolean {
  const allQueues = queue.getAllQueues();
  for (const lane of allQueues) {
    for (const entry of lane) {
      if (entry && isFrozen(entry)) return true;
    }
  }
  return false;
}

/**
 * Returns true when the game is "stuck on a frozen front":
 *   - no attacker on the conveyor (and none transitioning)
 *   - stack empty
 *   - every queue lane's front is either empty or a frozen attacker
 *   - at least one queue lane has a frozen attacker at its front (something to dispatch)
 *
 * In this state, the player has no legal move and frozen counters can't
 * decrement (they only tick when conveyor attackers exhaust). The runtime must
 * auto-dispatch the frozen front to keep the game playable. This mirrors
 * {@link StrategicSimulator}'s force-dispatch fallback and
 * {@link AtomicSimulator}'s `isStuckOnFrozenFront` escape hatch — keeping
 * runtime, bot simulator, and perfect solver all in agreement on winnability.
 *
 * Note: a frozen front may be a multi-orb attacker spanning multiple lanes. The
 * check is per-lane: each lane front independently must be either empty or frozen.
 */
export function isStuckOnFrozenFront(
  conveyorAttackerCount: number,
  transitioningCount: number,
  queue: QueueSystem,
  stack: StackSystem,
): boolean {
  if (conveyorAttackerCount > 0) return false;
  if (transitioningCount > 0) return false;
  if (stack.count() > 0) return false;

  let hasAnyFrozenFront = false;
  for (let lane = 0; lane < queue.numLanes(); lane++) {
    const q = queue.getQueueLane(lane);
    if (q.length === 0) continue; // empty lane is fine
    const front = q[0];
    if (front.isHammer) {
      // A hammer at the front means the game can make progress via
      // auto-trigger — not stuck.
      return false;
    }
    if (!isFrozen(front)) {
      // Non-frozen front exists → player has a legal dispatch → not stuck.
      return false;
    }
    hasAnyFrozenFront = true;
  }

  return hasAnyFrozenFront;
}

/**
 * Find the lowest-numbered queue lane whose front is a frozen attacker ready
 * to be force-dispatched (the attacker is at position 0 in all its lanes).
 * Returns null if the game is NOT in a stuck-on-frozen-front state.
 *
 * Use this with {@link QueueSystem.forceDispatchFrom} to auto-dispatch the
 * frozen front and resume play.
 */
export function findStuckOnFrozenFrontLane(
  conveyorAttackerCount: number,
  transitioningCount: number,
  queue: QueueSystem,
  stack: StackSystem,
): StuckOnFrozenFrontInfo | null {
  if (!isStuckOnFrozenFront(conveyorAttackerCount, transitioningCount, queue, stack)) {
    return null;
  }

  for (let lane = 0; lane < queue.numLanes(); lane++) {
    const attacker = queue.peekFrozenFront(lane);
    if (!attacker) continue;
    if (!isFrozen(attacker)) continue;
    return { laneIndex: lane, attackerId: attacker.id };
  }

  return null;
}
